// src/pages/veci/MensajesVeci.js
//
// Lista de conversaciones y vista de chat con polling cada 5 segundos.
// Rutas:
// - /veci/mensajes                  → lista
// - /veci/mensajes/:negocioId       → conversación con ese negocio

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Stack, Card, CardActionArea, CardContent, Avatar, Chip,
  Alert, CircularProgress, TextField, IconButton, Divider, Button,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import SendIcon         from '@mui/icons-material/Send';
import ChatBubbleIcon   from '@mui/icons-material/ChatBubble';
import StorefrontIcon   from '@mui/icons-material/Storefront';

import { chat } from '../../api/chatApi';
import { negocios as apiNegocios } from '../../api/negociosApi';
import { useApp } from '../../context/AppContext';

const POLLING_MS = 5000;

export default function MensajesVeci() {
  const { negocioId } = useParams();
  if (negocioId) return <VistaChat negocioId={parseInt(negocioId, 10)} />;
  return <ListaConversaciones />;
}

// ── Lista de conversaciones ─────────────────────────────────

function ListaConversaciones() {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');

  const cargar = useCallback(async () => {
    setError('');
    try {
      const r = await chat.conversaciones();
      const list = Array.isArray(r) ? r : (r.items || r.Items || []);
      setItems(list);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar las conversaciones.');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, POLLING_MS);
    return () => clearInterval(id);
  }, [cargar]);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Mensajes</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Tus conversaciones</Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <ChatBubbleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Aún no tienes conversaciones. Habla con un negocio para empezar.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/veci')}>Ver directorio</Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {items.map((c) => {
            const negId  = c.negocioId || c.NegocioId;
            const nombre = c.negocioNombre || c.NegocioNombre || c.contraparteNombre;
            const noLeidos = c.noLeidos || c.NoLeidos || 0;
            const ultimoTexto = c.ultimoMensaje || c.UltimoMensaje;
            const ultimaFecha = c.ultimaActividad || c.UltimaActividad;
            return (
              <Card key={c.conversacionId || c.ConversacionId}>
                <CardActionArea onClick={() => navigate(`/veci/mensajes/${negId}`)}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <StorefrontIcon />
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>{nombre}</Typography>
                      {ultimoTexto && (
                        <Typography variant="body2" color="text.secondary" noWrap>{ultimoTexto}</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      {ultimaFecha && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(ultimaFecha).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                      {noLeidos > 0 && <Chip label={noLeidos} size="small" color="primary" />}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

// ── Chat con un negocio ─────────────────────────────────────

function VistaChat({ negocioId }) {
  const navigate = useNavigate();
  const { sesionBackend } = useApp();
  const miVeciId = sesionBackend?.entidadId;

  const [negocio, setNegocio]         = useState(null);
  const [conversacionId, setConvId]   = useState(null);
  const [mensajes, setMensajes]       = useState([]);
  const [texto, setTexto]             = useState('');
  const [cargando, setCargando]       = useState(true);
  const [enviando, setEnviando]       = useState(false);
  const [error, setError]             = useState('');
  const contenedorRef = useRef(null);

  const cargarInicial = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const neg = await apiNegocios.detalle(negocioId);
      setNegocio(neg);

      // Buscar conversación existente
      const convs = await chat.conversaciones();
      const list = Array.isArray(convs) ? convs : (convs.items || []);
      const existente = list.find(c => (c.negocioId || c.NegocioId) === negocioId);
      if (existente) {
        const id = existente.conversacionId || existente.ConversacionId;
        setConvId(id);
        const det = await chat.detalle(id);
        setMensajes(det.mensajes || det.Mensajes || []);
        await chat.marcarVisto(id).catch(() => {});
      } else {
        setMensajes([]);
      }
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cargar el chat.');
    } finally { setCargando(false); }
  }, [negocioId]);

  useEffect(() => { cargarInicial(); }, [cargarInicial]);

  // Polling de mensajes
  useEffect(() => {
    if (!conversacionId) return;
    const tick = async () => {
      try {
        const det = await chat.detalle(conversacionId);
        setMensajes(det.mensajes || det.Mensajes || []);
      } catch { /* ignore */ }
    };
    const id = setInterval(tick, POLLING_MS);
    return () => clearInterval(id);
  }, [conversacionId]);

  // Auto-scroll al final cuando llegan mensajes nuevos
  useEffect(() => {
    if (contenedorRef.current) {
      contenedorRef.current.scrollTop = contenedorRef.current.scrollHeight;
    }
  }, [mensajes]);

  const enviar = async () => {
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      await chat.enviar({
        negocioId,
        texto: texto.trim(),
      });
      setTexto('');
      // Refresh mensajes inmediatos
      const convs = await chat.conversaciones();
      const list = Array.isArray(convs) ? convs : (convs.items || []);
      const existente = list.find(c => (c.negocioId || c.NegocioId) === negocioId);
      if (existente && !conversacionId) {
        setConvId(existente.conversacionId || existente.ConversacionId);
      }
      const cid = conversacionId || existente?.conversacionId || existente?.ConversacionId;
      if (cid) {
        const det = await chat.detalle(cid);
        setMensajes(det.mensajes || det.Mensajes || []);
      }
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo enviar el mensaje.');
    } finally { setEnviando(false); }
  };

  const nombreNeg = negocio?.nombre || negocio?.Nombre || 'Negocio';

  return (
    <Stack spacing={2} sx={{ height: 'calc(100vh - 120px)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => navigate('/veci/mensajes')}><ArrowBackIcon /></IconButton>
        <Avatar sx={{ bgcolor: 'primary.main' }}><StorefrontIcon /></Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>{nombreNeg}</Typography>
          <Typography variant="caption" color="text.secondary">Chat en vivo</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {/* Lista de mensajes */}
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 300 }}>
        <Box
          ref={contenedorRef}
          sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#FBF7F0' }}
        >
          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : mensajes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Aún no hay mensajes. Escribe el primero.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {mensajes.map((m, i) => {
                const emisorTipo = m.emisorTipo || m.EmisorTipo;
                const esMio = emisorTipo === 'VECI';
                const texto = m.texto || m.Texto || m.contenido;
                const fecha = m.fecha || m.Fecha || m.createdAt;
                return (
                  <Box key={i} sx={{ display: 'flex', justifyContent: esMio ? 'flex-end' : 'flex-start' }}>
                    <Box sx={{
                      bgcolor: esMio ? 'primary.main' : 'background.paper',
                      color: esMio ? 'primary.contrastText' : 'text.primary',
                      p: 1.5, borderRadius: 2, maxWidth: '75%',
                      boxShadow: 1,
                    }}>
                      <Typography variant="body2">{texto}</Typography>
                      {fecha && (
                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}>
                          {new Date(fecha).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Divider />

        {/* Composer */}
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Escribe un mensaje…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            multiline
            maxRows={4}
            size="small"
          />
          <IconButton onClick={enviar} disabled={enviando || !texto.trim()} color="primary">
            <SendIcon />
          </IconButton>
        </Box>
      </Card>
    </Stack>
  );
}
